/**
 * SPORT OPERATOR — Dashboard de saisie temps réel pour la PERFS WALL.
 * Login PIN -> sélectionner le match dans le calendrier -> sélection joueur + mode
 * (BUT / 2min / 7m) en un tap -> mi-temps / reprise / fin.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, ChevronRight, Trophy, Edit3, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const PIN_KEY = "iphb_operator_pin";

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function useOperatorClock(session) {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), 1000); return () => clearInterval(id); }, []);
  if (!session) return "--:--";
  const startMs = session.chrono_started_at ? new Date(session.chrono_started_at).getTime() : null;
  if (!startMs) return "--:--";
  const totalPaused = session.total_paused_ms || 0;
  let endRef;
  if (session.status === "halftime" && session.chrono_paused_at) endRef = new Date(session.chrono_paused_at).getTime();
  else if (session.status === "finished") endRef = session.finished_at ? new Date(session.finished_at).getTime() : Date.now();
  else endRef = Date.now();
  const elapsedMs = Math.max(0, endRef - startMs - totalPaused);
  const sec = Math.floor(elapsedMs / 1000);
  return `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;
}

function fmtMatchDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return iso; }
}

function PinLogin({ onAuth }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.get("/match-live/operator/check", { headers: { "X-Match-Operator-Pin": pin } });
      if (r.data?.ok) {
        localStorage.setItem(PIN_KEY, pin);
        onAuth(pin);
        toast.success("Authentifié comme opérateur");
      } else {
        toast.error("PIN incorrect");
      }
    } catch {
      toast.error("Erreur de connexion");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, #0D0220)` }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md" data-testid="operator-pin-form">
        <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Accès Opérateur</div>
        <h1 className="text-3xl font-black uppercase italic mb-1 text-white">Match Live</h1>
        <p className="text-white/60 text-sm mb-6">Saisissez le PIN opérateur fourni par l'administration.</p>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="••••"
          className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/15 text-white text-xl tracking-widest text-center focus:outline-none focus:border-pink-400"
          data-testid="operator-pin-input"
        />
        <button
          type="submit"
          disabled={busy || !pin}
          className="w-full mt-4 py-3 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          data-testid="operator-pin-submit"
        >
          {busy ? "Vérification..." : "Entrer"}
        </button>
        <Link to="/sport" className="block mt-4 text-center text-xs text-white/40 hover:text-white">← Retour au site</Link>
      </form>
    </div>
  );
}

// ============================================================
// StartForm — choix du match dans la liste, ou saisie manuelle
// ============================================================
function StartForm({ pin, onStarted }) {
  const [matches, setMatches] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({
    home_team: "Istres Provence Handball",
    home_logo: "",
    away_team: "",
    away_logo: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sport/team-roster?slug=istres-provence-handball")
      .then((r) => setRoster(r.data?.players || []))
      .catch(() => {});
    api.get("/sport/upcoming-matches?limit=10")
      .then((r) => setMatches(r.data?.matches || []))
      .catch(() => {})
      .finally(() => setLoadingMatches(false));
  }, []);

  const selected = useMemo(
    () => matches.find((m) => (m.id || m.date_iso) === selectedMatchId) || null,
    [matches, selectedMatchId],
  );

  const startFromMatch = async (m) => {
    setBusy(true);
    try {
      const homeTeam = m.home_team || "";
      const isIphbHome = homeTeam.toLowerCase().includes("istres");
      await api.post("/match-live/start", {
        game_id: m.id || null,
        home_team: homeTeam,
        home_logo: m.home_logo || "",
        away_team: m.away_team || "",
        away_logo: m.away_logo || "",
        home_roster: isIphbHome ? roster : [],
        away_roster: isIphbHome ? [] : roster,
      }, { headers: { "X-Match-Operator-Pin": pin } });
      toast.success("Match démarré · chrono lancé");
      onStarted();
    } catch {
      toast.error("Impossible de démarrer le match");
    } finally { setBusy(false); }
  };

  const startManual = async () => {
    if (!manual.away_team) { toast.error("Indiquez l'équipe adverse"); return; }
    setBusy(true);
    try {
      const isIphbHome = (manual.home_team || "").toLowerCase().includes("istres");
      await api.post("/match-live/start", {
        game_id: null,
        home_team: manual.home_team,
        home_logo: manual.home_logo,
        away_team: manual.away_team,
        away_logo: manual.away_logo,
        home_roster: isIphbHome ? roster : [],
        away_roster: isIphbHome ? [] : roster,
      }, { headers: { "X-Match-Operator-Pin": pin } });
      toast.success("Match démarré · chrono lancé");
      onStarted();
    } catch {
      toast.error("Impossible de démarrer le match");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-1 flex items-center gap-2">
        <CalendarDays size={12}/> Démarrer un match
      </div>
      <h1 className="text-3xl font-black uppercase italic text-white mb-6">Sélectionnez la rencontre</h1>

      {/* Liste matchs à venir */}
      {loadingMatches ? (
        <div className="text-white/50 italic">Chargement du calendrier…</div>
      ) : matches.length === 0 ? (
        <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-white/60 text-sm">
          Aucun match à venir dans le calendrier officiel.
        </div>
      ) : (
        <div className="space-y-2" data-testid="operator-match-list">
          {matches.map((m) => {
            const mid = m.id || m.date_iso;
            const isSelected = selectedMatchId === mid;
            const isIphbHome = (m.home_team || "").toLowerCase().includes("istres");
            return (
              <button
                key={mid}
                type="button"
                onClick={() => setSelectedMatchId(mid)}
                data-testid={`operator-match-${mid}`}
                className={`group w-full text-left rounded-xl border transition px-4 py-3 flex items-center gap-3 ${
                  isSelected
                    ? "bg-pink-500/15 border-pink-400 shadow-[0_0_0_2px_rgba(236,31,124,0.25)]"
                    : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/25"
                }`}
              >
                <div className="hidden sm:flex w-14 flex-col items-center text-center">
                  <div className="text-[10px] uppercase tracking-widest text-pink-300 font-black">{fmtMatchDate(m.date_iso)}</div>
                  <div className="text-white/70 text-sm font-bold tabular-nums">{m.time || "—"}</div>
                </div>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  {m.home_logo ? <img src={m.home_logo} alt="" className="w-7 h-7 object-contain shrink-0"/> : <div className="w-7 h-7 rounded-full bg-white/10 shrink-0"/>}
                  <div className={`text-sm font-bold truncate ${isIphbHome ? "text-pink-300" : "text-white"}`}>{m.home_team || "—"}</div>
                  <div className="text-white/40 text-xs mx-1">vs</div>
                  {m.away_logo ? <img src={m.away_logo} alt="" className="w-7 h-7 object-contain shrink-0"/> : <div className="w-7 h-7 rounded-full bg-white/10 shrink-0"/>}
                  <div className={`text-sm font-bold truncate ${!isIphbHome ? "text-pink-300" : "text-white"}`}>{m.away_team || "—"}</div>
                </div>
                {m.matchday && <div className="hidden md:block text-[10px] uppercase tracking-widest text-white/40 shrink-0">{String(m.matchday).toLowerCase().includes("journ") ? m.matchday : `J${m.matchday}`}</div>}
                <ChevronRight size={16} className={`shrink-0 transition ${isSelected ? "text-pink-300" : "text-white/40 group-hover:text-white/70"}`}/>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <button
          onClick={() => startFromMatch(selected)}
          disabled={busy}
          className="mt-5 w-full py-3 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          data-testid="operator-start-selected-btn"
        >
          <Trophy size={16}/>
          {busy ? "Démarrage..." : `Démarrer · ${selected.home_team} vs ${selected.away_team}`}
        </button>
      )}

      {/* Manuel — replié par défaut, pour amicaux / matchs hors calendrier */}
      <div className="mt-8 rounded-xl bg-white/5 border border-white/10">
        <button
          type="button"
          onClick={() => setManualOpen((o) => !o)}
          className="w-full px-4 py-3 flex items-center gap-2 text-left text-white/70 hover:text-white text-sm"
          data-testid="operator-manual-toggle"
        >
          <Edit3 size={14}/>
          <span className="font-bold uppercase tracking-wider text-xs">Match amical / saisie manuelle</span>
          <ChevronRight size={14} className={`ml-auto transition ${manualOpen ? "rotate-90" : ""}`}/>
        </button>
        <AnimatePresence>
          {manualOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Équipe domicile</label>
                    <input value={manual.home_team} onChange={(e) => setManual({ ...manual, home_team: e.target.value })} className="mt-1 w-full px-3 py-2 rounded bg-black/40 border border-white/15 text-white" data-testid="operator-start-home"/>
                    <input value={manual.home_logo} onChange={(e) => setManual({ ...manual, home_logo: e.target.value })} placeholder="URL logo (optionnel)" className="mt-2 w-full px-3 py-2 rounded bg-black/40 border border-white/15 text-white text-sm"/>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Équipe extérieure</label>
                    <input value={manual.away_team} onChange={(e) => setManual({ ...manual, away_team: e.target.value })} className="mt-1 w-full px-3 py-2 rounded bg-black/40 border border-white/15 text-white" data-testid="operator-start-away"/>
                    <input value={manual.away_logo} onChange={(e) => setManual({ ...manual, away_logo: e.target.value })} placeholder="URL logo (optionnel)" className="mt-2 w-full px-3 py-2 rounded bg-black/40 border border-white/15 text-white text-sm"/>
                  </div>
                </div>
                <button
                  onClick={startManual}
                  disabled={busy}
                  className="mt-4 w-full py-2.5 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50 bg-white/10 hover:bg-white/15"
                  data-testid="operator-start-manual-btn"
                >
                  {busy ? "Démarrage..." : "Démarrer (manuel)"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ============================================================
// LiveControls — sélection de joueur + mode (BUT / 2min / 7m)
// ============================================================
const MODES = [
  { k: "goal",      label: "BUT",         short: "+1",  color: "#EC1F7C", description: "Cliquez un joueur pour enregistrer un but" },
  { k: "shot_miss", label: "TIR MANQUÉ",  short: "✕",   color: "#94A3B8", description: "Cliquez un joueur dont le tir est non cadré / poteau / contré" },
  { k: "gk_save",   label: "ARRÊT GK",    short: "🧤",  color: "#22D3EE", description: "Cliquez le gardien IPHB qui a réalisé l'arrêt" },
  { k: "2min",      label: "2 MIN",       short: "2'",  color: "#FACC15", description: "Cliquez un joueur exclu 2 min" },
  { k: "7m",        label: "7M",          short: "7m",  color: "#F97316", description: "Cliquez un joueur ayant tiré un 7m" },
];

function LiveControls({ pin, session, refresh }) {
  const clock = useOperatorClock(session);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("goal");
  const activeMode = MODES.find((m) => m.k === mode) || MODES[0];

  const post = async (body) => {
    setBusy(true);
    try {
      await api.post("/match-live/event", { session_id: session.id, ...body }, { headers: { "X-Match-Operator-Pin": pin } });
      await refresh();
    } catch {
      toast.error("Action échouée");
    } finally { setBusy(false); }
  };

  // Évènement joueur côté IPHB (selon le mode choisi)
  const applyToPlayer = (player) => {
    post({
      kind: mode,
      side: iphbSide,
      player_name: player?.name || null,
      player_slug: player?.slug || null,
    });
  };

  const goalRevoke = (side) => post({ kind: "goal_revoke", side });
  const halftime = () => post({ kind: "halftime" });
  const resume = () => post({ kind: "resume" });
  const timeoutEv = (side) => post({ kind: "timeout", side });
  const oppEvent = (kind) => post({ kind, side: oppSide });
  const endMatch = async () => {
    if (!window.confirm("Terminer le match ?")) return;
    await post({ kind: "end" });
    toast.success("Match terminé");
  };

  const homeRoster = session.home_roster || [];
  const awayRoster = session.away_roster || [];
  const isIphbHome = (session.home_team || "").toLowerCase().includes("istres");
  const iphbSide = isIphbHome ? "home" : "away";
  const iphbRoster = isIphbHome ? homeRoster : awayRoster;
  const oppSide = isIphbHome ? "away" : "home";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-24">
      {/* Score & Chrono */}
      <div className="rounded-2xl p-5 bg-white/5 border border-white/10 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
            {session.status === "halftime" ? "Mi-temps" : session.status === "finished" ? "Terminé" : `${session.period}ʳᵉ MT`}
          </span>
          <span className="font-black tabular-nums text-2xl text-white" data-testid="operator-clock">{clock}</span>
        </div>
        <div className="grid grid-cols-3 items-center gap-2">
          <div className="text-center">
            <div className="text-xs uppercase text-white/60 truncate">{session.home_team}</div>
            <div className="text-5xl font-black tabular-nums text-white" data-testid="operator-home-score">{session.home_score}</div>
            <button onClick={() => goalRevoke("home")} disabled={busy || session.home_score === 0} className="mt-1 text-[10px] text-white/50 hover:text-pink-300 disabled:opacity-30">−1 (annuler)</button>
          </div>
          <div className="text-center text-white/30 text-3xl font-black">:</div>
          <div className="text-center">
            <div className="text-xs uppercase text-white/60 truncate">{session.away_team}</div>
            <div className="text-5xl font-black tabular-nums text-white" data-testid="operator-away-score">{session.away_score}</div>
            <button onClick={() => goalRevoke("away")} disabled={busy || session.away_score === 0} className="mt-1 text-[10px] text-white/50 hover:text-pink-300 disabled:opacity-30">−1 (annuler)</button>
          </div>
        </div>
      </div>

      {/* Chrono / mi-temps controls */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        {session.status === "playing" && session.period === 1 && (
          <button onClick={halftime} disabled={busy} className="col-span-2 py-3 rounded-lg font-bold uppercase tracking-wider bg-white/10 hover:bg-white/15 text-white" data-testid="operator-halftime-btn">⏸ Mi-temps</button>
        )}
        {session.status === "halftime" && (
          <button onClick={resume} disabled={busy} className="col-span-2 py-3 rounded-lg font-bold uppercase tracking-wider text-white" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }} data-testid="operator-resume-btn">▶ Reprise (2ᵉ MT)</button>
        )}
        {session.status === "playing" && session.period === 2 && (
          <button onClick={endMatch} disabled={busy} className="col-span-2 py-3 rounded-lg font-bold uppercase tracking-wider bg-red-500/80 hover:bg-red-500 text-white" data-testid="operator-end-btn">⏹ Fin du match</button>
        )}
        {session.status === "finished" && (
          <div className="col-span-2 py-3 rounded-lg text-center text-white/50 italic bg-white/5">Match terminé</div>
        )}
        <button onClick={endMatch} disabled={busy || session.status === "finished"} className="py-3 rounded-lg font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 text-white/70 text-xs">Forcer fin</button>
      </div>

      {/* Mode picker — détermine l'action déclenchée par un tap joueur */}
      <div className="mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Saisie joueur · choisissez l'action</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="operator-mode-picker">
          {MODES.map((m) => {
            const active = mode === m.k;
            return (
              <button
                key={m.k}
                type="button"
                onClick={() => setMode(m.k)}
                data-testid={`operator-mode-${m.k}`}
                className={`py-3 rounded-xl font-black uppercase tracking-wider text-sm transition border-2 ${
                  active ? "text-black scale-[1.02]" : "text-white/70 border-white/10 bg-white/5 hover:bg-white/10"
                }`}
                style={active ? { background: m.color, borderColor: m.color } : {}}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <div className="mt-2 text-[11px] text-white/55 italic" data-testid="operator-mode-hint">{activeMode.description}</div>
      </div>

      {/* IPHB roster grid — un click applique le mode au joueur */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Joueurs IPHB</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="operator-roster-grid">
          {iphbRoster.length === 0 && <div className="col-span-full text-white/40 text-sm italic">Aucun roster chargé pour ce match.</div>}
          {iphbRoster.map((p) => (
            <button
              key={p.slug || p.id || p.name}
              onClick={() => applyToPlayer(p)}
              disabled={busy || session.status !== "playing"}
              className="group relative p-2 rounded-xl bg-white/5 hover:bg-white/15 border border-white/10 transition disabled:opacity-40 text-left"
              data-testid={`operator-player-${p.slug || p.jersey_number}`}
              style={{ borderColor: `${activeMode.color}66` }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center font-black tabular-nums text-sm"
                  style={{ background: activeMode.color, color: mode === "goal" ? "#fff" : "#0D0220" }}
                >
                  {p.jersey_number || "—"}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{p.name}</div>
                  <div className="text-[10px] text-white/50 truncate">{p.position || ""}</div>
                </div>
              </div>
              <span
                className="absolute top-1 right-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ background: `${activeMode.color}33`, color: activeMode.color }}
              >
                {activeMode.short}
              </span>
            </button>
          ))}
        </div>
        {mode === "goal" && (
          <button
            onClick={() => applyToPlayer(null)}
            disabled={busy || session.status !== "playing"}
            className="mt-3 w-full py-2 rounded-lg text-sm font-bold uppercase tracking-wider text-white inline-flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
            data-testid="operator-goal-iphb-generic"
          >
            <Plus size={14}/> BUT IPHB (sans joueur identifié)
          </button>
        )}
      </div>

      {/* Adverse + autres événements */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Équipe adverse</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button onClick={() => oppEvent("goal")} disabled={busy || session.status !== "playing"} className="py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/15 text-white" data-testid="operator-goal-opp">+ BUT Adv.</button>
          <button onClick={() => oppEvent("2min")} disabled={busy || session.status !== "playing"} className="py-2.5 rounded-lg text-xs uppercase tracking-wider bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200" data-testid="operator-2min-opp">2 min Adv.</button>
          <button onClick={() => oppEvent("7m")} disabled={busy || session.status !== "playing"} className="py-2.5 rounded-lg text-xs uppercase tracking-wider bg-orange-500/20 hover:bg-orange-500/30 text-orange-200" data-testid="operator-7m-opp">7m Adv.</button>
          <button onClick={() => timeoutEv(oppSide)} disabled={busy || session.status !== "playing"} className="py-2.5 rounded-lg text-xs uppercase tracking-wider bg-blue-500/20 hover:bg-blue-500/30 text-blue-200">Timeout Adv.</button>
        </div>
      </div>

      {/* Timeout IPHB */}
      <div className="grid grid-cols-1 gap-2">
        <button onClick={() => timeoutEv(iphbSide)} disabled={busy || session.status !== "playing"} className="py-2.5 rounded-lg text-xs uppercase tracking-wider bg-blue-500/20 hover:bg-blue-500/30 text-blue-200">Timeout IPHB</button>
      </div>

      {/* Events feed */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Historique</div>
        <div className="space-y-1">
          {(session.events || []).slice().reverse().slice(0, 12).map((ev) => (
            <div key={ev.id} className="text-xs text-white/70 px-3 py-1.5 rounded bg-white/5 flex items-center gap-2">
              <span className="font-bold uppercase" style={{ color: ev.kind === "goal" ? SECONDARY : "#fff" }}>{ev.kind}</span>
              {ev.player_name && <span>{ev.player_name}</span>}
              <span className="ml-auto text-white/40">{ev.side || ""} · {new Date(ev.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SportOperator() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings: gs } = useSettings();
  const perfsWallEnabled = gs?.sport_perfs_wall_enabled !== false;

  // Vérifie le PIN sauvegardé au chargement
  useEffect(() => {
    if (!pin) { setLoading(false); return; }
    api.get("/match-live/operator/check", { headers: { "X-Match-Operator-Pin": pin } })
      .then((r) => { if (r.data?.ok) setAuthed(true); else { localStorage.removeItem(PIN_KEY); setPin(""); } })
      .catch(() => { localStorage.removeItem(PIN_KEY); setPin(""); })
      .finally(() => setLoading(false));
  }, [pin]);

  const refresh = async () => {
    try {
      const r = await api.get("/match-live/active");
      setSession(r.data?.session || null);
    } catch { /* swallow */ }
  };

  useEffect(() => {
    if (!authed) return;
    refresh();
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [authed]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-white/60" style={{ background: PRIMARY }}>Chargement…</div>;
  }

  if (!authed) return <PinLogin onAuth={(p) => { setPin(p); setAuthed(true); }} />;

  return (
    <div className="min-h-screen" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, #0D0220)` }}>
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Link to="/sport" className="text-white/60 hover:text-white text-sm">← Site</Link>
          <span className="text-white/30">·</span>
          <span className="text-xs uppercase tracking-[0.3em] text-white/70 font-bold">Opérateur Match</span>
        </div>
        <div className="flex items-center gap-3">
          {perfsWallEnabled && (
            <>
              <Link to="/sport/perfs-wall?demo=1" target="_blank" className="text-xs uppercase tracking-wider text-yellow-300 hover:text-yellow-200" data-testid="operator-link-demo">
                Mode démo ↗
              </Link>
              <Link to="/sport/perfs-wall" target="_blank" className="text-xs uppercase tracking-wider text-pink-300 hover:text-pink-200" data-testid="operator-link-perfs-wall">
                Perfs Wall ↗
              </Link>
            </>
          )}
          <button
            onClick={() => { localStorage.removeItem(PIN_KEY); setAuthed(false); setPin(""); }}
            className="text-xs text-white/50 hover:text-white"
            data-testid="operator-logout"
          >Déconnexion</button>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {!session || session.status === "finished" ? (
          <motion.div key="start" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {session?.status === "finished" && (
              <div className="max-w-2xl mx-auto px-6 pt-6 -mb-4">
                <div className="text-xs text-white/60 italic">Dernière session terminée — vous pouvez démarrer un nouveau match.</div>
              </div>
            )}
            <StartForm pin={pin} onStarted={refresh} />
          </motion.div>
        ) : (
          <motion.div key="live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <LiveControls pin={pin} session={session} refresh={refresh} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
